import { InventoryState } from '../../../src/feature/inventory/inventory.model';
import { items, ItemId } from './items';

// Base carry weight with no vessel (you carry in your arms). A backpack and,
// later, pack animals / carts add to this via each item's `capacityBonus`.
export const BASE_CAPACITY = 10;

const ids = (state: InventoryState) => Object.keys(state.items) as ItemId[];

export const getWeight = (state: InventoryState): number =>
  ids(state).reduce((sum, id) => sum + (items[id]?.weight ?? 0) * (state.items[id] ?? 0), 0);

// Weights step in tenths (e.g. a worm is 0.1). Round to a tenth and drop the
// trailing zero so display reads "0.5" / "4" rather than "0.5000000001" / "4.0".
export const formatWeight = (weight: number): string => String(Math.round(weight * 10) / 10);

// Capacity added by owned carry-vessels (backpack, future pack animals/carts).
export const getCapacityBonus = (state: InventoryState): number =>
  ids(state).reduce((sum, id) => sum + (items[id]?.capacityBonus ?? 0) * (state.items[id] ?? 0), 0);

export const getCapacity = (state: InventoryState): number => BASE_CAPACITY + getCapacityBonus(state);

// Can the player take `quantity` more of `id` without going over capacity?
// Weightless items (coins, vessels) always fit.
export const canCarry = (state: InventoryState, id: ItemId, quantity = 1): boolean => {
  const item = items[id];
  // Hard per-item stack cap (e.g. bait), independent of weight.
  if (item?.maxStack !== undefined && (state.items[id] ?? 0) + quantity > item.maxStack) return false;
  const added = (item?.weight ?? 0) * quantity;
  if (added <= 0) return true;
  return getWeight(state) + added <= getCapacity(state);
};
